import { useState, useEffect, useCallback } from 'react';
import {
  Activity, ToggleLeft, ToggleRight, AlertTriangle, CheckCircle,
  Loader2, Info, Cpu, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';

const formatDate = (d) => {
  if (!d) return 'Never';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/** A single queue-depth stat card. */
const StatCard = ({ label, value, color }) => (
  <div className="platform-card" style={{ padding: '1.25rem', textAlign: 'center' }}>
    <div style={{ fontSize: '2rem', fontWeight: 700, color: color || 'var(--text-primary, #e5e7eb)', lineHeight: 1.1 }}>
      {value ?? '—'}
    </div>
    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
      {label}
    </div>
  </div>
);

const PlatformInvestigationsTab = () => {
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [settingsRes, healthRes] = await Promise.all([
        fetch('/api/platform/investigations/engine/settings', { credentials: 'include' }),
        fetch('/api/platform/investigations/engine/health', { credentials: 'include' }),
      ]);
      if (settingsRes.ok) setStatus(await settingsRes.json());
      if (healthRes.ok) setHealth(await healthRes.json());
    } catch (err) {
      toast.error('Failed to load engine status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + auto-refresh every 15s.
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleToggle = async () => {
    if (status?.envVarOverride) {
      toast.warning('Engine is force-disabled by environment variable');
      return;
    }
    const newEnabled = !status.enabled;
    setToggling(true);
    try {
      const res = await fetch('/api/platform/investigations/engine/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: newEnabled }),
      });
      if (res.ok) {
        setStatus(await res.json());
        toast.success(newEnabled ? 'Correlation engine enabled' : 'Correlation engine disabled');
        fetchStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || 'Failed to update engine state');
      }
    } catch (err) {
      toast.error('Failed to update engine state');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="platform-loading">
        <Loader2 size={32} className="spin" />
      </div>
    );
  }

  const envOverride = status?.envVarOverride === true;
  const enabled = status?.enabled === true;

  let statusLabel, statusColor, StatusIcon;
  if (envOverride) {
    statusLabel = 'Engine forced OFF by environment variable';
    statusColor = '#f59e0b';
    StatusIcon = AlertTriangle;
  } else if (enabled) {
    statusLabel = 'Engine running';
    statusColor = '#10b981';
    StatusIcon = CheckCircle;
  } else {
    statusLabel = 'Engine disabled';
    statusColor = '#6b7280';
    StatusIcon = AlertTriangle;
  }

  return (
    <div className="platform-tab-content">
      <div className="platform-section">
        <div className="platform-section-header">
          <h2><Activity size={20} /> Correlation Engine</h2>
        </div>

        {/* ── Engine Status card ── */}
        <div className="platform-card" style={{ padding: '1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <StatusIcon size={20} style={{ color: statusColor }} />
                <strong style={{ color: statusColor }}>{statusLabel}</strong>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
                Disabling pauses all correlation activity. New ACS events will not be logged, no
                bookmarks will be created, and no actions dispatched. Existing CorrelationEvents
                and actions remain in the database. Re-enable to resume.
              </div>
              {status?.updatedBy && (
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                  Last changed by {status.updatedBy} on {formatDate(status.updatedAt)}
                </div>
              )}
            </div>

            <button
              onClick={handleToggle}
              disabled={toggling || envOverride}
              className={`btn ${enabled ? 'btn-success' : 'btn-secondary'}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                opacity: envOverride ? 0.5 : 1,
                cursor: envOverride ? 'not-allowed' : 'pointer',
              }}
              title={envOverride
                ? 'Remove DISABLE_CORRELATION_ENGINE env var and restart the service to use this toggle'
                : enabled ? 'Click to disable the correlation engine' : 'Click to enable the correlation engine'
              }
            >
              {toggling ? <Loader2 size={18} className="spin" />
                : enabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
              <span>{enabled ? 'Enabled' : 'Disabled'}</span>
            </button>
          </div>

          {envOverride && (
            <div style={{
              marginTop: '1rem', padding: '0.75rem',
              background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px',
              fontSize: '0.875rem', color: '#92400e',
              display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            }}>
              <Info size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>DISABLE_CORRELATION_ENGINE environment variable is forcing the engine off.</strong>
                <div>Remove the env var and restart the service to use this toggle.</div>
              </div>
            </div>
          )}
        </div>

        {/* ── Queue Depth: 4 stat cards in a row ── */}
        <h3 style={{ margin: '1.25rem 0 0.75rem', fontSize: '0.95rem' }}>Queue Depth</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <StatCard label="Pending Events"    value={health?.pendingEventCount}    color="#60a5fa" />
          <StatCard label="Processing Events" value={health?.processingEventCount} color="#a855f7" />
          <StatCard label="Failed Actions"    value={health?.failedActionCount}    color="#f59e0b" />
          <StatCard label="Permanently Failed Actions" value={health?.permanentlyFailedActionCount} color="#ef4444" />
        </div>

        {/* ── Workers card ── */}
        <h3 style={{ margin: '1.5rem 0 0.75rem', fontSize: '0.95rem' }}>Workers</h3>
        <div className="platform-card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Cpu size={13} /> Processor
              </div>
              <div style={{ fontWeight: 600, color: health?.processorRunning ? '#10b981' : '#ef4444' }}>
                {health?.processorRunning ? 'Running' : 'Stopped'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <RefreshCw size={13} /> Retry Worker
              </div>
              <div style={{ fontWeight: 600, color: health?.retryWorkerRunning ? '#10b981' : '#ef4444' }}>
                {health?.retryWorkerRunning ? 'Running' : 'Stopped'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Last Processed</div>
              <div style={{ fontWeight: 600 }}>{formatDate(health?.lastProcessedAt)}</div>
            </div>
          </div>
        </div>

        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <RefreshCw size={12} /> Auto-refreshes every 15 seconds.
        </div>
      </div>
    </div>
  );
};

export default PlatformInvestigationsTab;
